import { get, post } from '@/services/http';
import { PurchaseOrderModel, IPurchaseOrder } from '@/models/purchase_order';

export interface PurchaseOrdersFilter {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  source?: string;
  supplierId?: number;
  locationId?: number;
  fromDate?: string;
  toDate?: string;
  departureDateFrom?: string;
  departureDateTo?: string;
  expectedDeliveryDateFrom?: string;
  expectedDeliveryDateTo?: string;
  sortBy?: string;
  sortDir?: string;
}

interface CreateFromBuyingListOptions {
  expectedDeliveryDate?: string;
  notes?: string;
  locationId?: number;
  defaultTaxType?: { id: number; name: string; rate: number };
}

export class PurchaseOrders {
  public items: PurchaseOrderModel[] = [];
  public total: number = 0;

  public async fetch(filters: PurchaseOrdersFilter = {}): Promise<void> {
    const response: any = await get('/purchase_orders', filters);
    this.items = response.data.purchaseOrders.map(
      (po: IPurchaseOrder) => new PurchaseOrderModel(po),
    );
    console.log(this.items);
    this.total = response.data.pagination.total;
  }

  public async fetchAll(): Promise<void> {
    const response: any = await get('/purchase_orders', { limit: 1000 });
    this.items = response.data.purchaseOrders.map(
      (po: IPurchaseOrder) => new PurchaseOrderModel(po),
    );
    this.total = response.data.pagination.total;
  }

  public async fetchBySupplier(supplierId: number): Promise<void> {
    const response: any = await get('/purchase_orders', {
      supplierId,
    });
    this.items = response.data.purchaseOrders.map(
      (po: IPurchaseOrder) => new PurchaseOrderModel(po),
    );
    this.total = response.data.pagination.total;
  }

  public async fetchByStatus(status: string): Promise<void> {
    const response: any = await get('/purchase_orders', {
      status,
    });
    this.items = response.data.purchaseOrders.map(
      (po: IPurchaseOrder) => new PurchaseOrderModel(po),
    );
    this.total = response.data.pagination.total;
  }

  public async fetchPendingReceiving(): Promise<void> {
    const response: any = await get('/purchase_orders', {
      status: ['confirmed', 'partial_received'].join(','),
    });
    this.items = response.data.purchaseOrders.map(
      (po: IPurchaseOrder) => new PurchaseOrderModel(po),
    );
    this.total = response.data.pagination.total;
  }

  public async createNewPO(data: Partial<IPurchaseOrder>): Promise<PurchaseOrderModel> {
    const newPO = new PurchaseOrderModel(data);
    await newPO.save();
    this.items.push(newPO);
    return newPO;
  }

  public async createFromBuyingList(
    buyingListId: number,
    options: CreateFromBuyingListOptions = {},
  ): Promise<PurchaseOrderModel> {
    const newPO = await PurchaseOrderModel.createFromBuyingList(buyingListId, options);
    this.items.push(newPO);
    return newPO;
  }

  public async getOne(id: number | string): Promise<PurchaseOrderModel> {
    const response: any = await get(`/purchase_orders/${id}`);
    const purchaseOrder = new PurchaseOrderModel(response.data);
    // Fetch PO items
    await purchaseOrder.fetchItems();
    return purchaseOrder;
  }

  /**
   * Get purchase order by identifier (ID or PO number)
   * The backend auto-detects whether the identifier is numeric (ID) or string (PO number)
   *
   * @param identifier - Either a numeric ID (e.g., "12345") or PO number (e.g., "PO-2023-1023")
   */
  public async getByIdentifier(identifier: string): Promise<PurchaseOrderModel> {
    const response: any = await get(`/purchase_orders/lookup/${encodeURIComponent(identifier)}`);
    const purchaseOrder = new PurchaseOrderModel(response.data);
    return purchaseOrder;
  }

  /**
   * Get incoming inventory breakdown by variant ID
   * Returns list of POs with pending incoming quantity for a specific variant
   *
   * @param variantId - The Shopify variant ID
   */
  public async getIncomingByVariant(variantId: string): Promise<{
    variantId: string;
    totalIncoming: number;
    purchaseOrders: Array<{
      id: number;
      poNumber: string;
      orderDate: string | null;
      supplierName: string | null;
      locationId: number;
      departureDate: string | null;
      expectedDeliveryDate: string | null;
      incomingQuantity: number;
    }>;
  }> {
    const response: any = await get(`/purchase_orders/by-variant/${encodeURIComponent(variantId)}`);
    return response.data;
  }

  public async receiveItems(
    poId: number,
    itemUpdates: Array<{ id: number; receivedQuantity: number }>,
  ): Promise<PurchaseOrderModel> {
    const po = await this.getOne(poId);
    await po.receiveItems(itemUpdates);

    // Update the item in the collection if it exists
    const index = this.items.findIndex((item) => item.id === poId);
    if (index !== -1) {
      this.items[index] = po;
    }

    return po;
  }

  public getStatusOptions(): Array<{ text: string; value: string }> {
    return [
      { text: 'Draft', value: 'draft' },
      { text: 'Confirmed', value: 'confirmed' },
      { text: 'Partially Received', value: 'partial_received' },
      { text: 'Received', value: 'received' },
      { text: 'Completed', value: 'completed' },
      { text: 'Canceled', value: 'canceled' },
    ];
  }

  // Helper method to generate dashboard statistics
  public async getDashboardStats(): Promise<{
    total: number;
    pending: number;
    confirmed: number;
    received: number;
    value: {
      total: number;
      pending: number;
    }
  }> {
    const response: any = await get('/purchase_orders/stats');
    return response.data;
  }
}
